/**
 * UX Sprint 2 — Presentation & Delivery composite overview.
 *
 * Final-stage dashboard surfacing the 5 delivery artifacts:
 *   - Presentation deck
 *   - Video Prompts
 *   - Video Studio (manual generation surface)
 *   - Final Delivery
 *   - Share / Review Link
 *
 * Plus a quick Export Client PDF action that uses the existing
 * /api/exports/:id/report.pdf endpoint already wired into ProjectBoard.
 */
import { useEffect, useState } from "react";
import { FileDown, Presentation, FileText, Film, PackageCheck, Link2 } from "lucide-react";
import { toast } from "sonner";
import StageSummaryCard from "./StageSummaryCard";
import api, { API } from "@/lib/api";
import { prepareDownload, triggerSave } from "@/lib/exports";

function mapStatus(raw) {
    const s = (raw || "").toLowerCase();
    if (s === "approved") return "approved";
    if (["processing", "running", "in_progress", "leased"].includes(s)) return "in_progress";
    if (["pending", "completed", "manually_completed", "changes_requested", "rejected"].includes(s)) return "needs_review";
    if (s === "failed") return "failed";
    return "not_started";
}

export default function PresentationDeliveryWorkspace({ project, onOpenTab }) {
    const presentationStatus = mapStatus(project?.stages?.presentation?.status);
    const promptsStatus = mapStatus(project?.stages?.prompts?.status);
    const deliveryStatus = mapStatus(project?.stages?.final_delivery?.status);

    const [shareUrl, setShareUrl] = useState(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        let mounted = true;
        if (!project?.id) return;
        (async () => {
            try {
                const { data } = await api.get(`/projects/${project.id}/share`);
                if (mounted) setShareUrl(data?.share_url || null);
            } catch (e) { /* silent */ }
        })();
        return () => { mounted = false; };
    }, [project?.id]);

    const exportClientPdf = async () => {
        if (!project?.id || exporting) return;
        setExporting(true);
        try {
            const blob = await prepareDownload(`/exports/${project.id}/report.pdf`);
            triggerSave(blob, `client-report-${project.id}.pdf`);
            toast.success("Client PDF ready");
        } catch (e) {
            toast.error("Export failed — try again from the Presentation panel");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="px-6 lg:px-10 py-8" data-testid="presentation-delivery-workspace">
            <div className="mb-5 flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-micro mb-1 text-zinc-500">Presentation & Delivery · Overview</div>
                    <p className="text-sm text-zinc-400 max-w-2xl">
                        Generate the deck, prompt pack, and final delivery — then share the review link with the client.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={exportClientPdf}
                    disabled={exporting}
                    data-testid="delivery-export-client-pdf"
                    className="btn-primary text-xs px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                    <FileDown className="w-3.5 h-3.5" />
                    {exporting ? "Preparing…" : "Export Client PDF"}
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <StageSummaryCard
                    title="Presentation"
                    subtitle="Client-facing deck summarising brief → strategy → creative."
                    status={presentationStatus}
                    onOpen={() => onOpenTab && onOpenTab("presentation")}
                    openLabel="Open Presentation"
                    testid="delivery-card-presentation"
                >
                    <div className="flex items-start gap-2">
                        <Presentation className="w-4 h-4 mt-0.5 text-zinc-500" />
                        <span>
                            {presentationStatus === "approved" ? "Deck approved." :
                             presentationStatus === "needs_review" ? "Deck ready for your review." :
                             "Generate the deck from the Presentation panel."}
                        </span>
                    </div>
                </StageSummaryCard>

                <StageSummaryCard
                    title="Video Prompts"
                    subtitle="Per-shot generative prompts for downstream video tooling."
                    status={promptsStatus}
                    onOpen={() => onOpenTab && onOpenTab("video_prompts")}
                    openLabel="Open Video Prompts"
                    testid="delivery-card-prompts"
                >
                    <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 mt-0.5 text-zinc-500" />
                        <span>
                            {promptsStatus === "approved" ? "Prompt pack approved." :
                             promptsStatus === "needs_review" ? "Prompt pack generated — review and approve." :
                             "Generate the prompt pack from the Video Prompts panel."}
                        </span>
                    </div>
                </StageSummaryCard>

                <StageSummaryCard
                    title="Video Studio"
                    subtitle="Manual generation surface — one click, one render."
                    status="not_started"
                    onOpen={() => onOpenTab && onOpenTab("video_studio")}
                    openLabel="Open Video Studio"
                    testid="delivery-card-video-studio"
                >
                    <div className="flex items-start gap-2">
                        <Film className="w-4 h-4 mt-0.5 text-zinc-500" />
                        <span>Manual video generation lives here. Storyboard must be approved first.</span>
                    </div>
                </StageSummaryCard>

                <StageSummaryCard
                    title="Final Delivery"
                    subtitle="Bundled exports ready for handoff."
                    status={deliveryStatus}
                    onOpen={() => onOpenTab && onOpenTab("delivery")}
                    openLabel="Open Final Delivery"
                    testid="delivery-card-final"
                >
                    <div className="flex items-start gap-2">
                        <PackageCheck className="w-4 h-4 mt-0.5 text-zinc-500" />
                        <span>
                            {deliveryStatus === "approved" ? "Final delivery bundle approved." :
                             deliveryStatus === "needs_review" ? "Bundle ready — review and finalize." :
                             "Approve every stage to unlock final delivery."}
                        </span>
                    </div>
                </StageSummaryCard>

                <StageSummaryCard
                    title="Share / Review Link"
                    subtitle="Public read-only client review page."
                    status={shareUrl ? "approved" : "not_started"}
                    onOpen={() => onOpenTab && onOpenTab("share")}
                    openLabel="Open Share Link"
                    testid="delivery-card-share"
                >
                    <div className="flex items-start gap-2">
                        <Link2 className="w-4 h-4 mt-0.5 text-zinc-500" />
                        <span className="break-all">
                            {shareUrl ? <a href={shareUrl} target="_blank" rel="noreferrer" className="text-[#FF4400] hover:underline">{shareUrl}</a> : "Generate a share link from the Share panel."}
                        </span>
                    </div>
                </StageSummaryCard>
            </div>
        </div>
    );
}
